import React, { useState } from 'react';
import {
  AppBar,
  Avatar,
  Box,
  Chip,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import SchoolIcon from '@mui/icons-material/School';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import RateReviewIcon from '@mui/icons-material/RateReview';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import TuneIcon from '@mui/icons-material/Tune';
import { Outlet, useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { useThemeStore } from '../../store/themeStore';

const ROLE_COLORS: Record<string, 'default' | 'primary' | 'secondary' | 'error'> = {
  admin: 'error',
  content_manager: 'secondary',
  author: 'primary',
};

const DRAWER_WIDTH = 260;

export const AppLayout: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const { mode, toggleMode } = useThemeStore();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  // "Dashboard" removed — the logo navigates home
  const navLinks = [
    { label: 'Browse Lessons', to: '/browse',  icon: <LibraryBooksIcon />,        roles: ['admin', 'content_manager', 'author'] },
    { label: 'Reviews',        to: '/reviews', icon: <RateReviewIcon />,           roles: ['admin', 'content_manager'] },
    { label: 'Admin',          to: '/admin',          icon: <AdminPanelSettingsIcon />, roles: ['admin'] },
    { label: 'Prompt Manager', to: '/admin/prompts',  icon: <TuneIcon />,              roles: ['admin'] },
  ];

  const visibleLinks = navLinks.filter(
    (link) => user && link.roles.includes(user.role)
  );

  const handleMenuOpen = (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);
  const handleLogout = () => { handleMenuClose(); logout(); };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          bgcolor: mode === 'dark' ? 'background.paper' : 'primary.main',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Toolbar sx={{ gap: 1 }}>
          {/* Hamburger — opens nav drawer on all screen sizes */}
          <IconButton
            color="inherit"
            edge="start"
            onClick={() => setDrawerOpen(true)}
            aria-label="open navigation"
            sx={{ color: '#fff' }}
          >
            <MenuIcon />
          </IconButton>

          {/* Logo → navigates to /dashboard */}
          <Box
            component={Link}
            to="/dashboard"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              color: '#fff',
              textDecoration: 'none',
              flexGrow: 1,
            }}
          >
            <SchoolIcon sx={{ color: '#fff' }} />
            <Typography variant="h6" fontWeight={700} noWrap sx={{ color: '#fff' }}>
              LessonCraft AI
            </Typography>
          </Box>

          {/* Dark-mode toggle */}
          <IconButton
            color="inherit"
            onClick={toggleMode}
            aria-label="toggle dark mode"
            sx={{ color: '#fff' }}
          >
            {mode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
          </IconButton>

          {/* User avatar → dropdown */}
          <IconButton onClick={handleMenuOpen} aria-label="account menu" sx={{ p: 0.5 }}>
            <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main', fontSize: 14 }}>
              {user?.name?.charAt(0)?.toUpperCase() ?? 'U'}
            </Avatar>
          </IconButton>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            slotProps={{ paper: { sx: { minWidth: 200 } } }}
          >
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography variant="subtitle2" fontWeight={600}>{user?.name}</Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                {user?.email}
              </Typography>
              <Box sx={{ mt: 0.75 }}>
                <Chip
                  label={user?.role?.replace('_', ' ')}
                  size="small"
                  color={ROLE_COLORS[user?.role ?? 'author'] ?? 'default'}
                />
              </Box>
            </Box>
            <Divider />
            <MenuItem onClick={handleLogout}>Logout</MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Nav drawer — hidden by default, opened via hamburger */}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        slotProps={{ paper: { sx: { width: DRAWER_WIDTH } } }}
      >
        {/* Drawer header */}
        <Box
          sx={{
            px: 2,
            py: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            background: mode === 'dark'
              ? 'linear-gradient(135deg,#0C2541 0%,#003359 100%)'
              : 'linear-gradient(135deg,#003359 0%,#136DD3 100%)',
          }}
        >
          <SchoolIcon sx={{ color: '#fff' }} />
          <Typography variant="h6" fontWeight={700} sx={{ color: '#fff' }}>
            LessonCraft AI
          </Typography>
        </Box>

        <Divider />

        <List sx={{ pt: 1 }}>
          {visibleLinks.map((link) => {
            const isActive = location.pathname === link.to;
            return (
              <ListItemButton
                key={link.to}
                selected={isActive}
                onClick={() => { navigate(link.to); setDrawerOpen(false); }}
                sx={{
                  mx: 1,
                  borderRadius: 2,
                  mb: 0.5,
                  '&.Mui-selected': {
                    bgcolor: 'primary.main',
                    color: '#fff',
                    '& .MuiListItemIcon-root': { color: '#fff' },
                    '&:hover': { bgcolor: 'primary.dark' },
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 36,
                    color: isActive ? '#fff' : 'text.secondary',
                  }}
                >
                  {link.icon}
                </ListItemIcon>
                <ListItemText
                  primary={link.label}
                  primaryTypographyProps={{ fontWeight: isActive ? 700 : 400, fontSize: '0.9rem' }}
                />
              </ListItemButton>
            );
          })}
        </List>

        {/* Drawer footer — user info */}
        <Box sx={{ mt: 'auto', p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar sx={{ width: 36, height: 36, bgcolor: 'secondary.main', fontSize: 14 }}>
              {user?.name?.charAt(0)?.toUpperCase() ?? 'U'}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" fontWeight={600} noWrap>{user?.name}</Typography>
              <Typography variant="caption" color="text.secondary" noWrap display="block">
                {user?.role?.replace('_', ' ')}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Drawer>

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          mt: '64px',
          bgcolor: 'background.default',
          minHeight: 'calc(100vh - 64px)',
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
};
